/* eslint-disable @stencil-community/required-jsdoc */
import { Component, Host, h, Prop, State } from '@stencil/core';
import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';

import { sharingButtons } from './sharing-modal.data';

import type { SharingData, SharingTypes, SharingButton } from './sharing-modal.types';

import state from '../../../store/store';

@Component({
  tag: 'efb-sharing-modal',
  styleUrl: 'sharing-modal.scss',
  shadow: true,
})
export class EfbSharingModal {
  private modalRef: HTMLEfbBaseModalElement;

  /**
   * State
   */

  @State() currentUrl: string;

  @State() isSnackbarVisible = false;

  @State() activeVideoTrackingId: string = state.activeVideoTrackingId;

  /**
   * Props
   */

  @Prop() data: SharingData | string;

  @Memoize('data')
  get parsedData(): SharingData {
    return parseIfString(this.data);
  }

  @Prop() dataAnalyticsId: string;

  /**
   * Methods
   */

  private handleCloseClick() {
    this.modalRef.close();
  }

  private handleShareClick(type: SharingTypes) {
    if (type === 'clipboard') {
      this.copyToClipboard();
    } else {
      this.shareToSocials(type);
    }
  }

  private copyToClipboard() {
    // need permission first
    navigator.permissions
      .query({ name: 'clipboard-write' as PermissionName })
      .then(async (result) => {
        if (result.state === 'granted' || result.state === 'prompt') {
          try {
            await navigator.clipboard.writeText(this.currentUrl).then(() => {
              this.isSnackbarVisible = true;
              setTimeout(() => {
                this.isSnackbarVisible = false;
              }, 4000);
            });
          } catch (err) {
            // console.log('error', err);
          }
        }
      });
  }

  private shareToSocials(socialType: SharingTypes) {
    const encodedCurrentUrl = encodeURIComponent(this.currentUrl);
    const encodedTextMessage = encodeURIComponent(this.parsedData.sharingText);
    const facebookAppId = 990962682265748;
    let sharingLinkUrl: string;

    switch (socialType) {
      case 'whatsapp':
        sharingLinkUrl = `https://api.whatsapp.com/send?text=${encodedTextMessage}%3A%20${encodedCurrentUrl}`;
        break;

      case 'messenger':
        sharingLinkUrl = `https://www.facebook.com/dialog/send?link=${encodedCurrentUrl}&redirect_uri=${encodedCurrentUrl}&app_id=${facebookAppId}`;
        break;

      case 'facebook':
        sharingLinkUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedCurrentUrl}`;
        break;

      case 'twitter':
        sharingLinkUrl = `https://twitter.com/intent/tweet?url=${encodedCurrentUrl}&text=${encodedTextMessage}`;
        break;

      default:
        break;
    }

    window.open(sharingLinkUrl, 'myWindow', 'width=550, height=400');
  }

  /**
   * Lifecycle Hooks
   */

  componentDidLoad() {
    this.currentUrl = window.location.href;
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <efb-base-modal
          ref={(el) => {
            this.modalRef = el;
          }}
          modalId="sharing-modal"
        >
          <div class="sharing-modal" role="dialog" aria-labelledby="#sharingModalTitle">
            {/* Backdrop */}
            <span
              class="backdrop"
              aria-hidden="true"
              onClick={() => this.handleCloseClick()}
            ></span>

            {/* Content */}
            <div class="wrapper">
              {/* Snackbar Notification */}
              <div class={`snackbar-wrapper ${this.isSnackbarVisible && 'is-visible'}`}>
                <div class="snackbar">
                  <div class="snackbar-icon">
                    <efb-icon name="clipboard" />
                  </div>
                  {this.parsedData.clipboardNotification}
                </div>
              </div>

              <div class="content">
                {/* Close Button */}
                <efb-button
                  part="sharing-close-button"
                  text={this.parsedData.closeButton}
                  icon="close"
                  compact
                  ghost
                  onEfbButtonClick={() => this.handleCloseClick()}
                />

                {/* Title */}
                <div id="sharingModalTitle" class="title">
                  {this.parsedData.title}
                </div>

                {/* Sharing Buttons */}
                <ul class="button-group">
                  {sharingButtons.map((button: SharingButton) => (
                    <li class="button-group-item">
                      <efb-button
                        text={button.text}
                        icon={button.id}
                        hasLeftIcon
                        ghost
                        fullWidth
                        onEfbButtonClick={() => this.handleShareClick(button.id)}
                        dataAnalytics={
                          !isEmpty(this.dataAnalyticsId)
                            ? `b2c-campaign-page-multi-multi-first-timers-${this.dataAnalyticsId}${
                                !isEmpty(this.activeVideoTrackingId)
                                  ? `-${this.activeVideoTrackingId}`
                                  : ''
                              }_share-button_${button.id}`
                            : null
                        }
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </efb-base-modal>
      </Host>
    );
  }
}
